import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  SparklesIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  Cog6ToothIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';
import ReactMarkdown from 'react-markdown';
import toast from 'react-hot-toast';
import studiesService, { Study, StudyModule } from '../services/studies';
import { deckService } from '../services/deckService';
import quizService from '../services/quizService';
import matchService from '../services/matchService';
import LoadingSpinner from '../components/LoadingSpinner';
import UpgradeOptionsModal from '../components/UpgradeOptionsModal';

const markdownComponents = {
  code({ inline, className, children, ...props }: any) {
    if (inline) {
      return (
        <code
          className="px-1.5 py-0.5 rounded-md border border-purple-200 bg-purple-50 text-purple-800 dark:border-purple-800 dark:bg-purple-900/20 dark:text-purple-300 font-mono text-[0.85em] before:content-none after:content-none"
          {...props}
        >
          {children}
        </code>
      );
    }
    return (
      <code
        className={`font-mono text-sm text-gray-900 dark:text-gray-100 ${className || ''}`.trim()}
        {...props}
      >
        {children}
      </code>
    );
  },
  pre({ children, ...props }: any) {
    return (
      <pre
        className="my-4 overflow-x-auto rounded-lg border border-gray-200 bg-gray-100 p-4 text-gray-900 shadow-inner dark:border-gray-700 dark:bg-gray-900/80 dark:text-gray-100"
        {...props}
      >
        {children}
      </pre>
    );
  },
};

const splitModulePages = (content?: string): string[] => {
  if (!content || !content.trim()) return [];

  const normalized = content.replace(/\r\n/g, '\n').trim();
  const separatedPages = normalized
    .split(/\n\s*---\s*\n+/)
    .map((page) => page.trim())
    .filter(Boolean);
  const removePageHeading = (page: string) =>
    page.replace(/^\s*#{1,6}\s+Página\s+\d+\s*(?:\n|$)/i, '').trim();

  if (separatedPages.length > 1) {
    return separatedPages.map(removePageHeading);
  }

  return [removePageHeading(normalized)];
};

const StudyModuleDetail: React.FC = () => {
  const { id, moduleId } = useParams<{ id: string; moduleId: string }>();
  const navigate = useNavigate();
  const [study, setStudy] = useState<Study | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [upgradePromptMessage, setUpgradePromptMessage] = useState<string | null>(null);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [updatingModule, setUpdatingModule] = useState(false);
  const [previousViewedAt, setPreviousViewedAt] = useState<string | null>(null);
  const [activePage, setActivePage] = useState(1);
  const [creatingResource, setCreatingResource] = useState<'flashcard' | 'quiz' | 'match' | null>(null);

  const loadStudy = useCallback(async () => {
    if (!id || !moduleId) return;
    try {
      setLoading(true);
      let data = await studiesService.getStudy(id);
      // Visiting a module marks the study as in progress if it hasn't started yet
      if (data.status === 'not_started') {
        data = await studiesService.updateStudy(id, { status: 'in_progress' });
      }
      const currentModule = data.modules?.find(m => m._id === moduleId);
      setPreviousViewedAt(currentModule?.lastViewedAt || null);
      data = await studiesService.markModuleViewed(id, moduleId);
      setStudy(data);
    } catch (error) {
      toast.error('Erro ao carregar o estudo');
    } finally {
      setLoading(false);
    }
  }, [id, moduleId]);

  useEffect(() => {
    loadStudy();
  }, [loadStudy]);

  const module: StudyModule | undefined = study?.modules?.find(m => m._id === moduleId);
  const pages = useMemo(() => splitModulePages(module?.content), [module?.content]);

  useEffect(() => {
    setActivePage((current) => Math.min(Math.max(current, 1), Math.max(pages.length, 1)));
  }, [pages.length]);

  const handleGenerateContent = async (appendContent = false) => {
    if (!id || !moduleId) return;
    try {
      setGenerating(true);
      const updated = await studiesService.generateModuleContent(
        id,
        moduleId,
        appendContent ? { append: true } : {},
      );
      setStudy(updated);

      const nextModule = updated.modules?.find(m => m._id === moduleId);
      const nextPages = splitModulePages(nextModule?.content);
      const nextPageNumber = Math.max(nextPages.length, 1);
      setActivePage(nextPageNumber);
      toast.success(
        appendContent ? 'Nova página complementar adicionada com sucesso!' : 'Texto gerado com sucesso!'
      );
    } catch (error: any) {
      if (error?.response?.data?.code === 'AI_MODULE_LIMIT_REACHED') {
        setUpgradePromptMessage(
          error.response.data.message ||
            'Você atingiu o limite de módulos gerados com IA no plano gratuito.'
        );
      } else {
        toast.error(appendContent ? 'Erro ao criar conteúdo complementar' : 'Erro ao gerar o texto com IA');
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!id || !module) return;
    const nextStatus = module.status === 'completed' ? 'not_started' : 'completed';
    try {
      const updated = await studiesService.updateModuleStatus(id, module._id, nextStatus);
      setStudy(updated);
    } catch (error) {
      toast.error('Erro ao atualizar módulo');
    }
  };

  const openEditModal = () => {
    if (!module) return;
    setEditTitle(module.title);
    setEditDescription(module.description || '');
    setShowOptionsMenu(false);
    setShowEditModal(true);
  };

  const handleUpdateModule = async () => {
    if (!id || !moduleId || !editTitle.trim() || !editDescription.trim()) return;
    try {
      setUpdatingModule(true);
      const updated = await studiesService.updateModule(id, moduleId, {
        title: editTitle.trim(),
        description: editDescription.trim(),
      });
      setStudy(updated);
      setShowEditModal(false);
      toast.success('Módulo atualizado com sucesso!');
    } catch (error) {
      toast.error('Falha ao atualizar módulo');
    } finally {
      setUpdatingModule(false);
    }
  };

  const handleDeleteModule = async () => {
    if (!id || !moduleId) return;
    try {
      setDeleting(true);
      await studiesService.deleteModule(id, moduleId);
      toast.success('Módulo excluído com sucesso!');
      navigate(`/studies/${id}`);
    } catch (error) {
      toast.error('Falha ao excluir módulo');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const getActivePageText = () => {
    if (!pages.length) return module?.content || '';
    return pages[activePage - 1] || pages[0] || '';
  };

  const resourceContext = (pageText: string) => {
    const baseText = pageText.trim();
    const topic = module?.title || 'Conteúdo de estudo';
    const summary = module?.description ? `${module.description}\n\n${baseText}` : baseText;
    const contentContext = summary.trim().slice(0, 6000);

    return {
      topic,
      description: `Crie material de estudo com base neste texto do módulo "${topic}".\n\n${contentContext}`,
      additionalInfo: `Contexto da criação: módulo "${topic}".\n\n${contentContext}`,
    };
  };

  const handleCreateResource = async (resource: 'flashcard' | 'quiz' | 'match') => {
    if (!module) return;

    const pageText = getActivePageText();
    if (!pageText.trim()) {
      toast.error('Não há texto neste módulo para gerar conteúdo.');
      return;
    }

    const context = resourceContext(pageText);
    setCreatingResource(resource);

    try {
      if (resource === 'flashcard') {
        const result = await deckService.requestAIDeck({
          topic: context.topic,
          description: context.description,
          cardCount: 15,
          difficulty: 'intermediate',
          language: 'portuguese',
          additionalInfo: context.additionalInfo,
        });

        toast.success(result.message || 'Flashcards criados com sucesso!');
        if (result.deckId) {
          navigate(`/decks/management/${result.deckId}`);
          return;
        }
        navigate('/decks');
        return;
      }

      if (resource === 'quiz') {
        const result = await quizService.requestAIQuiz({
          topic: context.topic,
          description: context.description,
          questionCount: 15,
          difficulty: 'intermediate',
          language: 'portuguese',
          additionalInfo: context.additionalInfo,
        });

        toast.success(result.message || 'Quiz criado com sucesso!');
        if (result.quizId) {
          navigate(`/quizzes/management/${result.quizId}`);
          return;
        }
        navigate('/quizzes');
        return;
      }

      const result = await matchService.requestAIMatch({
        topic: context.topic,
        pairsCount: 10,
        difficulty: 'medium',
        language: 'portuguese',
      });

      toast.success(result.message || 'Match criado com sucesso!');
      if (result.matchGameId) {
        navigate(`/matches/management/${result.matchGameId}`);
        return;
      }
      navigate('/matches');
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Erro ao criar o recurso de estudo.';
      toast.error(message);
    } finally {
      setCreatingResource(null);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!study || !module) {
    return (
      <div className="max-w-6xl mx-auto px-2 sm:px-4 lg:px-6 py-4 sm:py-8 text-center">
        <p className="text-gray-600 dark:text-gray-400">Módulo não encontrado.</p>
        <button
          onClick={() => navigate(`/studies/${id}`)}
          className="mt-4 inline-flex items-center text-blue-600 dark:text-blue-400 hover:underline"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Voltar para Módulos
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-2 sm:px-4 lg:px-6 py-4 sm:py-8">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => navigate(`/studies/${id}`)}
          className="inline-flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Voltar para Módulos
        </button>

        <div className="relative">
          <button
            onClick={() => setShowOptionsMenu(!showOptionsMenu)}
            className="inline-flex items-center justify-center p-2 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700"
            title="Configurações"
          >
            <Cog6ToothIcon className="h-5 w-5" />
          </button>

          {showOptionsMenu && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowOptionsMenu(false)}
              ></div>
              <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-20 overflow-hidden">
                <button
                  onClick={openEditModal}
                  className="w-full flex items-center px-4 py-3 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <PencilIcon className="h-4 w-4 mr-2" />
                  Editar módulo
                </button>
                <button
                  onClick={() => {
                    setShowOptionsMenu(false);
                    setShowDeleteConfirm(true);
                  }}
                  className="w-full flex items-center px-4 py-3 text-sm text-left text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  <TrashIcon className="h-4 w-4 mr-2" />
                  Excluir Módulo
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="flex items-start justify-between mb-2">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{module.title}</h1>
        <button
          onClick={handleToggleStatus}
          title={module.status === 'completed' ? 'Marcar como não concluído' : 'Marcar como concluído'}
          className="flex-shrink-0 ml-2"
        >
          {module.status === 'completed' ? (
            <CheckCircleIconSolid className="h-7 w-7 text-green-500" />
          ) : (
            <CheckCircleIcon className="h-7 w-7 text-gray-300 dark:text-gray-600 hover:text-green-500" />
          )}
        </button>
      </div>
      {module.description && (
        <p className="text-gray-600 dark:text-gray-400 mb-6">{module.description}</p>
      )}

      {previousViewedAt && (
        <p className="text-xs text-gray-500 dark:text-gray-500 mb-6 -mt-4">
          Última visualização: {new Date(previousViewedAt).toLocaleString('pt-BR')}
        </p>
      )}

      {!module.content ? (
        <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <SparklesIcon className="mx-auto h-12 w-12 text-purple-500" />
          <h3 className="mt-3 text-lg font-medium text-gray-900 dark:text-white">
            Nenhum texto criado ainda
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Deixe a IA escrever o conteúdo de estudo deste módulo.
          </p>
          <button
            onClick={() => handleGenerateContent(false)}
            disabled={generating}
            className="mt-6 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 disabled:opacity-50"
          >
            {generating ? (
              <>
                <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                Gerando texto...
              </>
            ) : (
              <>
                <SparklesIcon className="h-5 w-5 mr-2" />
                Gerar Texto com IA
              </>
            )}
          </button>
        </div>
      ) : (
        <>
          <div className="mb-4 text-sm font-medium text-gray-600 dark:text-gray-400">
            Página {activePage} de {pages.length || 1}
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8">
            <div
              className="prose prose-base sm:prose-lg dark:prose-invert max-w-none
                prose-headings:font-semibold prose-headings:text-gray-900 dark:prose-headings:text-white
                prose-h1:text-2xl prose-h2:text-xl prose-h2:mt-8 prose-h2:border-b prose-h2:border-gray-200 dark:prose-h2:border-gray-700 prose-h2:pb-2
                prose-h3:text-lg prose-p:leading-relaxed prose-p:text-gray-700 dark:prose-p:text-gray-300
                prose-strong:text-gray-900 dark:prose-strong:text-white
                prose-a:text-purple-600 dark:prose-a:text-purple-400 prose-a:no-underline hover:prose-a:underline
                prose-li:text-gray-700 dark:prose-li:text-gray-300 prose-li:marker:text-purple-500
                prose-blockquote:border-purple-400 prose-blockquote:text-gray-600 dark:prose-blockquote:text-gray-400
                prose-pre:bg-transparent prose-pre:p-0"
            >
              <ReactMarkdown components={markdownComponents}>{pages[activePage - 1] || module.content}</ReactMarkdown>
            </div>

            <div className="mt-6 grid grid-cols-1 items-center gap-3 sm:grid-cols-3">
              <div className="flex flex-col items-center text-center sm:col-start-2">
                <p className="mb-1 text-sm text-gray-500 dark:text-gray-400">
                  Quer aprender mais sobre este tema?
                </p>
                <button
                  type="button"
                  onClick={() => handleGenerateContent(true)}
                  disabled={generating}
                  className="group inline-flex items-center gap-2 rounded-md px-2 py-1 text-sm font-semibold text-blue-600 transition-colors hover:text-violet-700 hover:underline hover:underline-offset-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:text-blue-400 dark:hover:text-violet-300 dark:focus-visible:ring-offset-gray-800 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:no-underline"
                >
                  {generating ? (
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  ) : (
                    <SparklesIcon className="h-4 w-4 transition-transform group-hover:rotate-12" />
                  )}
                  Criar mais conteúdo
                </button>
              </div>

              {pages.length > 1 && (
                <div className="flex justify-center gap-3 sm:col-start-3 sm:justify-self-end">
                  <button
                    type="button"
                    onClick={() => setActivePage((page) => Math.max(page - 1, 1))}
                    disabled={activePage === 1}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Página anterior
                  </button>
                  <button
                    type="button"
                    onClick={() => setActivePage((page) => Math.min(page + 1, pages.length))}
                    disabled={activePage === pages.length}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Próxima página
                  </button>
                </div>
              )}
            </div>

            <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-5">
              <div className="flex items-center justify-between gap-3 mb-3">
                <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">
                  Criar a partir deste texto
                </h3>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <button
                  type="button"
                  onClick={() => handleCreateResource('flashcard')}
                  disabled={creatingResource !== null}
                  className="inline-flex items-center justify-center px-4 py-3 text-sm font-medium rounded-lg border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 dark:border-violet-800 dark:bg-violet-900/20 dark:text-violet-300 dark:hover:bg-violet-900/30 disabled:opacity-50"
                >
                  Criar flashcards
                </button>
                <button
                  type="button"
                  onClick={() => handleCreateResource('quiz')}
                  disabled={creatingResource !== null}
                  className="inline-flex items-center justify-center px-4 py-3 text-sm font-medium rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300 dark:hover:bg-blue-900/30 disabled:opacity-50"
                >
                  Criar quiz
                </button>
                <button
                  type="button"
                  onClick={() => handleCreateResource('match')}
                  disabled={creatingResource !== null}
                  className="inline-flex items-center justify-center px-4 py-3 text-sm font-medium rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300 dark:hover:bg-emerald-900/30 disabled:opacity-50"
                >
                  Criar matches
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {creatingResource && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-900/60 px-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white p-6 text-center shadow-2xl dark:bg-gray-800">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/30">
              <SparklesIcon className="h-7 w-7 text-purple-600 dark:text-purple-300 animate-pulse" />
            </div>
            <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
              {creatingResource === 'flashcard' && 'Criando flashcards...'}
              {creatingResource === 'quiz' && 'Criando quiz...'}
              {creatingResource === 'match' && 'Criando matches...'}
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              Estamos transformando este texto em material de estudo com o contexto padrão selecionado.
            </p>
            <div className="mt-5 flex justify-center">
              <LoadingSpinner />
            </div>
          </div>
        </div>
      )}

      {upgradePromptMessage && (
        <UpgradeOptionsModal
          title="Limite de módulos com IA atingido"
          message={upgradePromptMessage}
          feature="módulos de estudo ilimitados com IA"
          icon="lock"
          onClose={() => setUpgradePromptMessage(null)}
        />
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-75 overflow-y-auto h-full w-full z-50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="relative border border-gray-200 dark:border-gray-700 w-full max-w-sm p-5 shadow-lg dark:shadow-2xl dark:shadow-black/50 rounded-lg bg-white dark:bg-gray-800">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20">
                <TrashIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mt-4">
                Excluir Módulo?
              </h3>
              <div className="mt-2 px-2 py-3">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Tem certeza que deseja excluir este módulo? Todo o conteúdo gerado será perdido. Esta ação não pode ser desfeita.
                </p>
              </div>
              <div className="flex justify-center space-x-3 mt-4">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200 text-sm font-medium rounded-md shadow-sm hover:bg-gray-400 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600 dark:focus:ring-offset-gray-800 transition-colors duration-200"
                  disabled={deleting}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteModule}
                  className="px-4 py-2 bg-red-600 dark:bg-red-700 text-white text-sm font-medium rounded-md shadow-sm hover:bg-red-700 dark:hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-offset-gray-800 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={deleting}
                >
                  {deleting ? 'Excluindo...' : 'Excluir'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditModal && module && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-75 overflow-y-auto h-full w-full z-50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="relative border border-gray-200 dark:border-gray-700 w-full max-w-md p-5 shadow-lg dark:shadow-2xl dark:shadow-black/50 rounded-lg bg-white dark:bg-gray-800">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              Editar módulo
            </h3>
            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Título *
                </label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(event) => setEditTitle(event.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm px-3 py-2"
                  autoFocus
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Tópico *
                </label>
                <textarea
                  value={editDescription}
                  onChange={(event) => setEditDescription(event.target.value)}
                  rows={4}
                  required
                  className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm px-3 py-2"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-5">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200 text-sm font-medium rounded-md shadow-sm hover:bg-gray-400 dark:hover:bg-gray-500"
                disabled={updatingModule}
              >
                Cancelar
              </button>
              <button
                onClick={handleUpdateModule}
                disabled={updatingModule || !editTitle.trim() || !editDescription.trim()}
                className="px-4 py-2 bg-purple-600 dark:bg-purple-500 text-white text-sm font-medium rounded-md shadow-sm hover:bg-purple-700 dark:hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updatingModule ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudyModuleDetail;
